import Component from '../../components/Component';
import Loading from '../../components/Loading';
import DataTable from '../../components/Datatable';
import { v4 as uuid } from 'uuid';
import { COLUMNS, getStrength } from './keys';
import { scrollForTable } from '../../components/Datatable/utils';
import React from 'react';
import ExtraBox from './extraBox';
import AdditionalBox from './additionalBox';
import { withTranslation } from 'react-i18next';

function findParts(acc, curr, ids) {
    const parts = curr.path.split('/');
    if (parts.length > 1) {
        const parent = parts.shift();
        return findParts(
            acc.nodes.find((item) => item.name === parent),
            { ...curr, path: parts.join('/') },
            ids,
        );
    } else {
        const id = uuid();
        ids.push(id);
        if (acc.nodes) {
            acc.nodes.push({ ...curr, name: parts[0], id });
        } else {
            acc.nodes = [{ ...curr, name: parts[0], id }];
        }
    }
    return acc;
}

function createTree(data) {
    if (!data || data?.length === 0) return { nodes: [], ids: [] };
    const ids = [];
    const collectedData = data.reduce((acc, curr) => {
        const levelParts = curr.level.slice(0, -1).split('/');
        levelParts.shift();
        if (levelParts.length === 1) {
            const id = uuid();
            ids.push(id);
            acc.push({ ...curr, name: levelParts[0], id });
        } else if (levelParts.length >= 2) {
            const parent = levelParts.shift();

            findParts(
                acc.find((item) => item.name === parent),
                { ...curr, path: levelParts.join('/') },
                ids,
            );
        }
        return acc;
    }, []);
    const allData = [
        {
            name: 'All Categories',
            sum_impressions: null,
            sum_clicks: null,
            avg_ctr: null,
            unique_pages: null,
            nodes: collectedData.map((item) => ({ ...item, haveStrength: true })),
            id: 'all_categories',
        },
    ];
    return { nodes: allData, ids };
}

function getNestedView(data, length) {
    if (!data || data.length === 0) return [];
    const collectedView = data
        .filter((item) => item.level.split('/').length === length)
        .map((item) => ({ ...item, name: `all${item.level}`, id: uuid(), haveStrength: true }));

    return collectedView;
}

const tabs = (t) => [
    {
        icon: 'icon-fi_grid',
        label: t('discover.categories.opportunities.all'),
        key: 'all',
        hint: '',
    },
    {
        icon: 'icon-grid-03',
        label: t('discover.categories.opportunities.strong_categories_2'),
        key: 'strong_categories_2',
        hint: 'These are the 2nd level categories that have high median impression per article, continue writing about them',
        view: 2,
    },
    {
        icon: 'icon-file-04',
        label: t('discover.categories.opportunities.striking_distance_2'),
        key: 'striking_distance_2',
        hint: 'These are the 2nd level categories that have high median impression per article but low number of articles, test writing about them',
        view: 2,
    },
    {
        icon: 'icon-intersect-circle',
        label: t('discover.categories.opportunities.strong_categories_3'),
        key: 'strong_categories_3',
        hint: 'These are the 3rd level categories that have high median impression per article, continue writing about them',
        view: 3,
    },
    {
        icon: 'icon-arrows-right',
        label: t('discover.categories.opportunities.striking_distance_3'),
        key: 'striking_distance_3',
        hint: 'These are the 2nd level categories that have high median impression per article but low number of articles, test writing about them',
        view: 3,
    },
];

class Table extends Component {
    constructor(props) {
        super();
        this.checkIsEmptyAll = this.checkIsEmptyAll.bind(this);
        this.chooseView = this.chooseView.bind(this);
        this.getViewData = this.getViewData.bind(this);
        this.onToggle = this.onToggle.bind(this);

        this.ids = React.createRef();

        this.images = {
            strong: props.image_strong,
            striking_distance: props.image_striking_distance,
            weak: props.image_weak,
        };
    }

    componentDidUpdate(prevProps) {
        if (JSON.stringify(prevProps.data) !== JSON.stringify(this.props.data)) {
            const { nodes, ids } = createTree(this.props.data);
            this.ids.current = ids;
            this.setState({
                nodes,
                ids,
            });
        }
    }
    checkIsEmptyAll(list) {
        for (let element of list) {
            if (!element.classList.contains('hide-component')) return false;
        }
        return true;
    }

    getAverages(data) {
        const length = data?.length ?? 1;
        const sumAll = data?.reduce((acc, curr, i) => {
            if (i === 0) {
                acc = {
                    sumPages: curr.unique_pages,
                    sumImpressions: curr.sum_impressions,
                    sumClicks: curr.sum_clicks,
                    sumCTR: curr.avg_ctr,
                };
            } else {
                acc = {
                    sumPages: acc.sumPages + curr.unique_pages,
                    sumImpressions: acc.sumImpressions + curr.sum_impressions,
                    sumClicks: acc.sumClicks + curr.sum_clicks,
                    sumCTR: acc.sumCTR + curr.avg_ctr,
                };
            }
            return acc;
        }, {});
        const averages = {
            avgPages: sumAll.sumPages / length,
            avgImpressions: sumAll.sumImpressions / length,
            avgClicks: sumAll.sumClicks / length,
            avgCTR: sumAll.sumCTR / length,
        };
        return averages;
    }

    chooseView(view) {
        this.setState({ view });
    }

    getViewData(nodes) {
        switch (this.state.view) {
            case 2: {
                const data = getNestedView(this.props.data, 4);
                const { avgPages, avgImpressions, avgClicks, avgCTR } = this.getAverages(data);
                return {
                    nodes: data,
                    avgPages,
                    avgImpressions,
                    avgClicks,
                    avgCTR,
                    levelName: '2nd Level',
                };
            }
            case 3: {
                const data = getNestedView(this.props.data, 5);
                const { avgPages, avgImpressions, avgClicks, avgCTR } = this.getAverages(data);
                return {
                    nodes: data,
                    avgPages,
                    avgImpressions,
                    avgClicks,
                    avgCTR,
                    levelName: 'third level',
                };
            }
            default: {
                const nesteddData = nodes[0]?.nodes || [];
                const { avgPages, avgImpressions, avgClicks, avgCTR } =
                    this.getAverages(nesteddData);

                return {
                    nodes,
                    avgPages,
                    avgImpressions,
                    avgClicks,
                    avgCTR,
                    levelName: 'top level',
                };
            }
        }
    }

    searchFunction(data, search) {
        const filteredData = data?.filter((item) =>
            item.level?.toLowerCase().includes(search?.toLowerCase()),
        );
        const withParents = data.filter((item) => {
            const parent = filteredData.find((state) =>
                state.level?.toLowerCase().includes(item.level?.toLowerCase()),
            );
            if (parent) return true;

            return false;
        });

        const { nodes, ids } = createTree(withParents);
        this.ids.current = ids;

        return nodes;
    }

    onToggle(value, view) {
        if (value === 'all') {
            this.setState({
                toggle: value,
                sortedData: null,
                sortKey: 'sum_impressions',
            });
        } else if (value === 'striking_distance_2' || value === 'striking_distance_3') {
            const sortedData = this.props.data.filter(
                (item) => item.unique_pages >= 5 && item.unique_pages <= 9,
            );
            const data = getNestedView(sortedData, view + 2);
            const { avgPages, avgImpressions, avgClicks, avgCTR } = this.getAverages(data);
            this.setState({
                toggle: value,
                sortKey: 'median_impressions_per_page',
                sortedData: {
                    nodes: data,
                    avgPages,
                    avgImpressions,
                    avgClicks,
                    avgCTR,
                    levelName: view === 2 ? '2nd Level' : 'third level',
                },
            });
        } else {
            const sortedData = this.props.data.filter((item) => item.unique_pages >= 10);
            const data = getNestedView(sortedData, view + 2);
            const { avgPages, avgImpressions, avgClicks, avgCTR } = this.getAverages(data);
            this.setState({
                toggle: value,
                sortKey: 'median_impressions_per_page',
                sortedData: {
                    nodes: data,
                    avgPages,
                    avgImpressions,
                    avgClicks,
                    avgCTR,
                    levelName: view === 2 ? '2nd Level' : 'third level',
                },
            });
        }
        this.chooseView(view);
    }

    render() {
        const { t } = this.props;

        if (this.props.loading) {
            return <Loading padding={10} />;
        }

        const extraBox = (hasTree, tree) => (
            <ExtraBox
                view={this.state.view}
                hasTree={hasTree}
                tree={tree}
                chooseView={this.chooseView}
                ids={this.ids}
                isConfidence={this.props.isConfidence}
                toggle={this.props.toggle}
                avgPages={avgPages}
                avgCTR={avgCTR}
                avgImpressions={avgImpressions}
                avgClicks={avgClicks}
                isDark={this.state.isDark}
                t={t}
            />
        );
        const additionalBox = (hasTree, tree) => (
            <AdditionalBox
                view={this.state.view}
                hasTree={hasTree}
                tree={tree}
                chooseView={this.chooseView}
                ids={this.ids}
                isConfidence={this.props.isConfidence}
                toggle={this.props.toggle}
                avgPages={avgPages}
                avgCTR={avgCTR}
                avgImpressions={avgImpressions}
                avgClicks={avgClicks}
                t={t}
            />
        );

        if (!this.state.nodes) return null;

        const { nodes, avgPages, avgImpressions, avgClicks, avgCTR } =
            this.state.sortedData || this.getViewData(this.state.nodes);
        const averages = { avgPages, avgImpressions, avgClicks, avgCTR };

        const sortFns = {
            category: (array) => array.sort((a, b) => a.name.localeCompare(b.name)),
            unique_pages: (array) =>
                array.sort((a, b) => {
                    return a.unique_pages - b.unique_pages;
                }),
            sum_impressions: (array) => array.sort((a, b) => a.sum_impressions - b.sum_impressions),
            sum_clicks: (array) => array.sort((a, b) => a.sum_clicks - b.sum_clicks),
            avg_ctr: (array) =>
                array.sort(
                    (a, b) => a.sum_clicks / a.sum_impressions - b.sum_clicks / b.sum_impressions,
                ),
            strength: (array) =>
                array.sort((a, b) => getStrength(a, averages) - getStrength(b, averages)),
            median_impressions_per_page: (array) =>
                array.sort((a, b) => a.median_impressions_per_page - b.median_impressions_per_page),
        };

        return (
            <>
                <div className="card rounded-main overflow-hidden opportunity-box">
                    <div className="card-body">
                        <div className="box-header">
                            {t('discover.categories.opportunities.header')}
                        </div>
                        <div className="opportunity-box-items">
                            {tabs(t).map((item) => (
                                <button
                                    key={item.key}
                                    type="button"
                                    className={`opportunity-box-item ${
                                        (this.state.toggle ?? 'all') === item.key ? 'active' : ''
                                    }`}
                                    title={item.hint}
                                    onClick={() => this.onToggle(item.key, item.view)}
                                >
                                    {item.label}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
                <div
                    className="card table-main rounded-main overflow-hidden"
                    style={{ overflow: 'hidden' }}
                >
                    <DataTable
                        nodes={nodes}
                        sortFns={sortFns}
                        openedItemIds={['all_categories']}
                        COLUMNS={COLUMNS(averages, this.images, t)}
                        fileName={t('discover.categories.table.header')}
                        header={t('discover.categories.table.header')}
                        extraBtns={extraBox}
                        additionalBox={additionalBox}
                        hasTree={!this.state.view || this.state.view === 1}
                        searchFunction={
                            !this.state.view || this.state.view === 1
                                ? (data, search) => this.searchFunction(this.props.data, search)
                                : null
                        }
                        forceRun={this.props.forceRun}
                        defaultSortKey={this.state.sortKey || 'sum_impressions'}
                        additionalStyles={{
                            Table: `
                                --data-table-library_grid-template-columns : minmax(400px, 3fr) minmax(120px, 1fr) minmax(150px, 1fr) minmax(150px, 1fr) minmax(120px, 1fr) minmax(120px, 1fr) minmax(120px, 1fr) !important;
                                ${scrollForTable}
                            `,
                            BaseCell: ` 
                                font-family: 'Raleway';
                                font-weight: 700;
                                font-size: 16px;
                                line-height: 19px;
                                padding: 16px 10px !important;
                                color: #0D182C;
                            
                            
                                &.underline {
                                    text-decoration: underline;
                                }
                            `,
                        }}
                    />
                </div>
            </>
        );
    }
}

export default withTranslation()(Table);
